import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Smartphone, Loader2, Send, Info, X } from "lucide-react";
import { toast } from "sonner";
import {
  enablePush,
  disablePush,
  hasActiveSubscription,
  isPushSupported,
  getPushPermission,
} from "../lib/pushClient";
import { pushApi, formatError } from "../lib/api";

/**
 * Enable / disable phone-push notifications for THIS device.
 *
 * Behaviour rules:
 *  - If the browser lacks Push API support, render an "unsupported" panel
 *    with a helpful iOS Add-to-Home-Screen hint.
 *  - If permission has been permanently blocked (`Notification.permission === "denied"`),
 *    tell the user they must unblock in browser settings — we can't re-prompt.
 *  - Subscription toggle is local to THIS device: the user might have push
 *    on at home but off on their work laptop.
 */
export const PushEnableCard = ({ disabled = false }) => {
  const supported = isPushSupported();
  const [permission, setPermission] = useState(() => getPushPermission());
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [iosHelpOpen, setIosHelpOpen] = useState(false);

  const isIOSSafari = typeof navigator !== "undefined" &&
    /iP(hone|ad|od)/.test(navigator.userAgent) &&
    /Safari/.test(navigator.userAgent) &&
    !/CriOS|FxiOS|EdgiOS/.test(navigator.userAgent);
  const isStandalone = typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.navigator?.standalone === true);

  const refresh = useCallback(async () => {
    if (!supported) { setStatusLoaded(true); return; }
    const [active] = await Promise.all([hasActiveSubscription()]);
    setSubscribed(active);
    setPermission(getPushPermission());
    setStatusLoaded(true);
  }, [supported]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleEnable = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await enablePush();
      setSubscribed(true);
      setPermission(getPushPermission());
      toast.success("Phone alerts on for this device");
    } catch (err) {
      toast.error(err?.message || "Couldn't enable phone alerts");
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await disablePush();
      setSubscribed(false);
      toast.success("Phone alerts off for this device");
    } catch (err) {
      toast.error(formatError(err) || "Couldn't disable phone alerts");
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async () => {
    if (testing) return;
    setTesting(true);
    try {
      const res = await pushApi.test();
      toast.success(
        `Test push sent to ${res.delivered_to_devices} device${res.delivered_to_devices === 1 ? "" : "s"}`
      );
    } catch (err) {
      toast.error(formatError(err) || "Test push failed");
    } finally {
      setTesting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.15 }}
      className="mt-6"
      data-testid="notif-push-section"
    >
      <div className="flex items-center gap-2 mb-2 px-1">
        <Smartphone className="w-3.5 h-3.5 text-[#8A93A6]" />
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8A93A6]">
          Phone alerts
        </div>
      </div>
      <div className="tup-card p-5">
        <div className="flex items-start gap-3">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
            subscribed ? "brand-gradient-bg" : "bg-[#F5F3ED]"
          }`}>
            <Smartphone className={`w-5 h-5 ${subscribed ? "text-white" : "text-[#8A93A6]"}`} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-heading text-base font-semibold text-[#2D3142]">
              Push notifications
            </div>
            <p className="text-xs text-[#4F5D75] mt-1 leading-relaxed">
              Get lock-screen alerts on this device the moment food is about to expire — no email needed.
            </p>

            {!supported && (
              <div
                className="mt-3 text-[11px] text-[#9C3E1D] bg-[#FBE9DD] border border-[#E07A5F]/40 rounded-xl px-3 py-2 leading-snug"
                data-testid="notif-push-unsupported"
              >
                Your browser doesn&apos;t support push notifications.
                {isIOSSafari && !isStandalone && (
                  <>
                    {" "}
                    <button
                      type="button"
                      onClick={() => setIosHelpOpen(true)}
                      className="underline font-semibold"
                      data-testid="notif-push-ios-help-btn"
                    >
                      iOS users tap here
                    </button>
                    .
                  </>
                )}
              </div>
            )}

            {supported && permission === "denied" && (
              <div
                className="mt-3 text-[11px] text-[#9C3E1D] bg-[#FBE9DD] border border-[#E07A5F]/40 rounded-xl px-3 py-2 leading-snug"
                data-testid="notif-push-denied"
              >
                You blocked notifications for this site. Unblock QFresh in your browser settings,
                then reload the page to enable phone alerts.
              </div>
            )}
          </div>
        </div>

        {supported && permission !== "denied" && statusLoaded && (
          <div className="mt-4 grid grid-cols-2 gap-2">
            {!subscribed ? (
              <button
                type="button"
                onClick={handleEnable}
                disabled={busy || disabled}
                data-testid="notif-push-enable-btn"
                className="col-span-2 h-12 rounded-2xl brand-gradient-bg text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-colors"
              >
                {busy ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Enabling…</>
                ) : (
                  <><Smartphone className="w-4 h-4" /> Enable on this device</>
                )}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleTest}
                  disabled={testing || disabled}
                  data-testid="notif-push-test-btn"
                  className="h-11 rounded-2xl bg-white border-2 border-[#277A82] text-[#1F5566] font-semibold flex items-center justify-center gap-1.5 disabled:opacity-60 transition-colors hover:bg-[#DCEDF0]"
                >
                  {testing ? (
                    <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending…</>
                  ) : (
                    <><Send className="w-3.5 h-3.5" /> Test push</>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleDisable}
                  disabled={busy || disabled}
                  data-testid="notif-push-disable-btn"
                  className="h-11 rounded-2xl bg-white border-2 border-[#E07A5F]/50 text-[#9C3E1D] font-semibold flex items-center justify-center gap-1.5 disabled:opacity-60 transition-colors hover:bg-[#FBE9DD]"
                >
                  Turn off on this device
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* iOS Add-to-Home-Screen help modal */}
      <AnimatePresence>
        {iosHelpOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setIosHelpOpen(false)}
            className="fixed inset-0 z-[70] bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center px-4"
            data-testid="notif-push-ios-help-modal-overlay"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              transition={{ type: "spring", stiffness: 280, damping: 28 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-[440px] bg-[#FDFBF7] rounded-3xl p-6 relative"
              data-testid="notif-push-ios-help-modal"
            >
              <button
                onClick={() => setIosHelpOpen(false)}
                className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-[#F5F3ED] flex items-center justify-center text-[#4F5D75]"
                aria-label="Close"
                data-testid="notif-push-ios-help-close-btn"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="w-12 h-12 rounded-xl bg-[#DCEDF0] flex items-center justify-center">
                <Info className="w-6 h-6 text-[#1F5566]" />
              </div>
              <h2 className="font-heading text-xl font-bold text-[#2D3142] mt-3">
                Add QFresh to your Home Screen
              </h2>
              <p className="text-sm text-[#4F5D75] mt-3 leading-relaxed">
                On iPhone, Safari can only send push notifications after you install QFresh as a home-screen app.
              </p>
              <ol className="mt-4 space-y-2 text-sm text-[#2D3142] leading-relaxed list-decimal pl-5">
                <li>Tap the <strong>Share</strong> icon at the bottom of Safari.</li>
                <li>Scroll and tap <strong>Add to Home Screen</strong>.</li>
                <li>Open QFresh from your home screen, then come back here to turn on push.</li>
              </ol>
              <p className="text-[11px] text-[#8A93A6] mt-4 leading-relaxed">
                Requires iOS 16.4 or later. Once installed, alerts land on your lock screen just like a native app.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
